import { createContext, useContext } from 'react';
import { components } from '../../api/api-abcscale';

export type SkuEntitlements = components['schemas']['SkuPermissionsDto'];

// Empty object = no explicit denials → everything is allowed.
// Components use a deny-only model: a permission is restricted only when explicitly set to false.
const DEFAULT_ENTITLEMENTS: SkuEntitlements = {};

export const SkuEntitlementsContext = createContext<SkuEntitlements>(DEFAULT_ENTITLEMENTS);
// Separate context so consumers can wait before rendering entitlement-gated UI.
export const SkuEntitlementsLoadedContext = createContext<boolean>(false);

export function useSkuEntitlements(): SkuEntitlements {
  return useContext(SkuEntitlementsContext);
}

export function useSkuEntitlementsLoaded(): boolean {
  return useContext(SkuEntitlementsLoadedContext);
}

// ── Permission helpers ──────────────────────────────────────────────────────
//
// Each function returns true unless the backend explicitly set the flag to false.
//   false     → denied
//   true      → allowed
//   undefined → allowed (permission not configured; defaults to open)
//
// To audit what controls each UI element, search for the helper name (e.g. "can.addSku").

export const can = {
  // ── products ───────────────────────────────────────────────────────────────
  // Governs: "Búsqueda Productos" tab visibility; "Ver precios" buttons within it.
  searchProducts:           (p: SkuEntitlements) => p.products?.search                !== false,

  // ── sku ────────────────────────────────────────────────────────────────────
  // Governs: "Búsqueda Precios" tab visibility; individual "Ver precios" buttons when
  //          Búsqueda Productos is shown alongside it.
  searchSku:                (p: SkuEntitlements) => p.sku?.search                     !== false,
  // Governs: "+ Nuevo SKU" button.
  addSku:                   (p: SkuEntitlements) => p.sku?.add                        !== false,
  // Governs: "Simular descuento a distribuidor" checkbox and panel; distributor
  //          discount slider inside the SkuCard Resumen edit form.
  simulateCustomerDiscount: (p: SkuEntitlements) => p.sku?.simulateCustomerDiscount   !== false,
  // Governs: "General" section pill on SkuCard.
  viewSkuDetails:           (p: SkuEntitlements) => p.sku?.viewDetails                !== false,
  // Governs: "Editar" button inside the General panel.
  updateSkuDetails:         (p: SkuEntitlements) => p.sku?.updateDetails              !== false,
  // Governs: "Resumen" (graficas) section pill on SkuCard.
  viewSkuSummary:           (p: SkuEntitlements) => p.sku?.viewSummary                !== false,
  // Governs: "Editar Precio Venta" button inside the Resumen panel.
  updateSkuPrice:           (p: SkuEntitlements) => p.sku?.updatePrice                !== false,
  // Governs: "Desglose" section pill on SkuCard.
  viewSkuBreakdown:         (p: SkuEntitlements) => p.sku?.viewBreakdown              !== false,
  // Governs: "Envío" section pill on SkuCard.
  viewSkuShipment:          (p: SkuEntitlements) => p.sku?.viewShipment               !== false,
  // Governs: "Editar" button inside the Envío panel.
  updateSkuShipment:        (p: SkuEntitlements) => p.sku?.updateShipment             !== false,
  // Governs: "Importación" section pill on SkuCard.
  viewSkuImportCosts:       (p: SkuEntitlements) => p.sku?.viewImportCosts            !== false,
  // Governs: "Editar" button inside the Importación panel.
  updateSkuImportCosts:     (p: SkuEntitlements) => p.sku?.updateImportCosts          !== false,
  // Governs: "Descuento" section pill on SkuCard.
  viewSkuDiscount:          (p: SkuEntitlements) => p.sku?.viewDiscount               !== false,

  // ── discounts ──────────────────────────────────────────────────────────────
  // Governs: "Descuentos" tab visibility.
  viewDiscounts:            (p: SkuEntitlements) => p.discounts?.view                 !== false,
  // Governs: "+ Nuevo Descuento" button.
  addDiscount:              (p: SkuEntitlements) => p.discounts?.add                  !== false,
  // Governs: Edit and Delete buttons in the Discounts table.
  updateDiscount:           (p: SkuEntitlements) => p.discounts?.update               !== false,
  // Governs: "Asignar un nuevo descuento" collapsible section in AssignDiscountPanel.
  assignDiscount:           (p: SkuEntitlements) => p.discounts?.assign               !== false,
  // Governs: "Desasignar" button in the assigned-discounts table.
  unassignDiscount:         (p: SkuEntitlements) => p.discounts?.unassign             !== false,

  // ── composite ──────────────────────────────────────────────────────────────
  // True when at least one PriceList tab is accessible; used to hide the sidebar nav link.
  anyPriceListTab: (p: SkuEntitlements) =>
    p.products?.search !== false ||
    p.sku?.search      !== false ||
    p.discounts?.view  !== false,
  // True only when the user can see all SKU data sections — required to export a meaningful quote.
  // Governs: "Exportar" button in PriceListPriceSearch results header.
  exportQuote: (p: SkuEntitlements) =>
    p.sku?.viewDetails     !== false &&
    p.sku?.viewSummary     !== false &&
    p.sku?.viewBreakdown   !== false &&
    p.sku?.viewShipment    !== false &&
    p.sku?.viewImportCosts !== false &&
    p.sku?.viewDiscount    !== false,
};
